import React, { useEffect, useMemo, useRef, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import ConfirmationModal from "Components/Dialogs/ConfirmationModal";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import BadgeStatus from "Components/Generic/BadgeStatus";
import Breadcrumb from "Components/Generic/Breadcrump";
import SubHeader from "Components/Generic/SubHeader";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import {
  ManageEmailSubscriptionEditorDrawer,
  ManageEmailSubscriptionEditorDrawerHandles,
} from "Components/ManageEmailSubscriptionEditorDrawer";
import {
  ManageEmailSubscriptionViewerDrawer,
  ManageEmailSubscriptionViewerDrawerHandles,
} from "Components/ManageEmailSubscriptionViewerDrawer";
import { PRIVATE_ROUTES } from "Constants";
import { useAppState } from "Context/StateContext";
import { format } from "date-fns-tz";
import { Edit as EditIcon, SmsTracking as SmsTrackingIcon } from "iconsax-react";
import { ContactCronjob, Cronjob } from "Models/cronjob";
import { Error } from "Models/error";
import { Row, Searches } from "Models/shipeezi.table";
import { User } from "Models/user";
import { useTranslation } from "react-i18next";
import { AiOutlineWarning } from "react-icons/ai";
import ReportManagementService from "Services/analytics.report.service";
import { onSaveItemSelected } from "Util/onSaveItemSelected";
import { ReportManagement } from "Util/queries";

import { MainDiv, IconButton, ModalText } from "./styled";
import { CronjobStatus } from "Util/enums";
import { Tooltip } from "@mui/material";
import SmsNotificationError from "Components/Generic/Icons/SmsNotificationError";
import { colors } from "@zapalacorp/tokens";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { isMoving } from "Util/validation";

export const ManageEmailSubscriptionsPage = () => {
  const { t: translate } = useTranslation();
  const { currentUser, userPermissions } = useAppState();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [rows, setRows] = useState<Row[]>([]);
  const [selectedCronJob, setSelectedCronJob] = useState<Cronjob>();
  const [searches, setSearches] = useState<Searches>([]);

  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [emailSubscriptionPermission, setEmailSubscriptionPermission] = useState<{
    activities: boolean;
    deactivate: boolean;
  }>({
    activities: false,
    deactivate: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setEmailSubscriptionPermission({
        activities:
          checkResourceCan(userPermissions, ResourceName.ANALYTICS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_REPORTS_GENERAL_ACTIVITIES),
        deactivate:
          checkResourceCan(userPermissions, ResourceName.ANALYTICS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_REPORTS_GENERAL_DEACTIVATE),
      });
    }
  }, [userPermissions]);

  const manageEmailSubscriptionViewerDrawerRef =
    useRef<ManageEmailSubscriptionViewerDrawerHandles>(null);
  const manageEmailSubscriptionEditorDrawerRef =
    useRef<ManageEmailSubscriptionEditorDrawerHandles>(null);

  const getBreadCrumbItems = () => {
    return [
      {
        title: translate("analytics"),
        link: {
          url: PRIVATE_ROUTES.ANALYTICS,
        },
      },
      {
        title: translate("manageEmailSubscriptions"),
        link: {
          url: PRIVATE_ROUTES.MANAGE_EMAIL_SUBSCRIPTIONS,
        },
      },
    ];
  };

  const handleSaveCronJobItems = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, reportManagementList);

    setRows([...rowsSelected]);
  };

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const {
    refetch: getReportManagementList,
    data: reportManagementList,
    isFetching,
  } = useQuery({
    queryKey: ReportManagement.getAll([page, pageSize, searches]),
    queryFn: async () => {
      return ReportManagementService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          { association: "user" },
          { association: "cronjobContacts", include: [{ association: "user" }] },
          { association: "histories", include: [{ association: "user" }] },
        ]),
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
      }).then(({ data }) => data);
    },
    initialData() {
      return [];
    },
    enabled: !!currentUser?.companyId,
  });

  const { data: reportManagementTotal } = useQuery({
    queryKey: ReportManagement.count([searches]),
    queryFn: async () => {
      return await ReportManagementService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
      }).then(({ data }) => data);
    },
    initialData() {
      return 0;
    },
    enabled: !!currentUser?.companyId,
  });

  const { mutate: deactivateSubscription, isLoading: isDeactivatingCronJob } = useMutation({
    mutationKey: ReportManagement.deleteSubscription(),
    mutationFn: (variables: Cronjob) => ReportManagementService.deleteManagement(variables),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      setOpenDeactivateModal(false);
      getReportManagementList();
    },
    onError: (error: AxiosError<Error>) => {
      if (error && error.response) NotificationToast.error(error?.response?.data?.message);
    },
  });

  const { mutate: deactivateSubscriptionList, isLoading: isDeactivatingCronJobList } = useMutation({
    mutationKey: ReportManagement.deleteSubscription(),
    mutationFn: (variables: Cronjob[]) => ReportManagementService.deleteManagementList(variables),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      getReportManagementList();
    },
    onError: (error: AxiosError<Error>) => {
      if (error && error.response) NotificationToast.error(error?.response?.data?.message);
    },
  });

  const { mutate: updateManagement, isLoading: isUpdating } = useMutation({
    mutationKey: ReportManagement.create(),
    mutationFn: (cronJob: Partial<Cronjob> & { changes?: string }) =>
      ReportManagementService.updateManagement(cronJob),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      manageEmailSubscriptionEditorDrawerRef.current?.close();
      getReportManagementList();
    },
  });

  const columns: Column[] = useMemo(() => {
    return [
      {
        name: "id",
        title: translate("id"),
        formatText: (text: string) => `${text}`,
      },
      { name: "frequency", title: translate("frequency") },
      { name: "name", title: translate("reportName") },
      {
        name: "cronjobContacts",
        title: translate("subscribers"),
        formatText: (contact: ContactCronjob[]) => {
          if (!contact.length) return "";

          const users = contact.map((contact) => contact.user);

          return users.map((user) => user?.fullName).join(", ");
        },
      },
      {
        name: "user",
        title: translate("createdBy"),
        formatText: (user: User) => (user && user.fullName ? user.fullName : "-"),
      },
      {
        name: "updatedAt",
        title: translate("lastUpdated"),
        formatText: (text: string) => (text ? format(new Date(text), "dd/MM/yyyy hh:mm a") : "-"),
      },
      {
        name: "createdAt",
        title: translate("createdOn"),
        formatText: (text: string) => (text ? format(new Date(text), "dd/MM/yyyy hh:mm a") : "-"),
      },
      {
        name: "status",
        title: translate("status"),
        formatComponent: (status: string) => (status ? <BadgeStatus value={status} /> : null),
      },
    ];
  }, []);

  const handleEditEmailSubscription = (subscription: Cronjob) => {
    manageEmailSubscriptionEditorDrawerRef.current?.open(subscription as any);
  };

  const handleShowEmailSubscriptionActivities = (subscription: Cronjob) => {
    manageEmailSubscriptionViewerDrawerRef.current?.open(subscription);
  };

  const handleDeactivateEmailSubscription = (subscription: Cronjob) => {
    setSelectedCronJob(subscription);
    setOpenDeactivateModal(true);
  };

  const returnButtonToDeactivateEmailSubscription = () => {
    const isDisabled =
      !rows.length ||
      !!rows.filter((row) =>
        [CronjobStatus.COMPLETED, CronjobStatus.DEACTIVATED].includes(
          row.status?.toLowerCase() as any
        )
      )?.length;
    return (
      <Button
        customColor={Color.ERROR}
        startIcon={<SmsNotificationError width={16} height={16} color={colors.white} />}
        disabled={isDisabled}
        onClick={() => deactivateSubscriptionList(rows as Cronjob[])}
      >
        {translate("deactivate")}
      </Button>
    );
  };

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [
      {
        title: translate("edit"),
        key: "edit",
        buttonAndAction: (_, subscription: Cronjob) => {
          if (
            subscription.status &&
            [CronjobStatus.COMPLETED, CronjobStatus.DEACTIVATED].includes(
              subscription.status?.toLowerCase() as any
            )
          )
            return null;

          return (
            <Tooltip title={translate("edit")} placement="top">
              <IconButton onClick={() => handleEditEmailSubscription(subscription as any)}>
                <EditIcon size={16} />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ];

    if (isShowAction(currentUser, emailSubscriptionPermission.activities)) {
      actions?.push({
        title: translate("activities"),
        key: "activities",
        buttonAndAction: (_, subscription: Cronjob) => {
          return (
            <Tooltip title={translate("activities")} placement="top">
              <IconButton
                onClick={() => handleShowEmailSubscriptionActivities(subscription)}
                size="large"
              >
                <SmsTrackingIcon size={16} />
              </IconButton>
            </Tooltip>
          );
        },
      });
    }

    if (isShowAction(currentUser, emailSubscriptionPermission.deactivate)) {
      actions?.push({
        title: translate("deactivate"),
        key: "deactivate",
        buttonAndAction: (_, subscription: Cronjob) => {
          if (
            subscription.status &&
            [CronjobStatus.COMPLETED, CronjobStatus.DEACTIVATED].includes(
              subscription.status?.toLowerCase() as any
            )
          )
            return null;

          return (
            <Tooltip title={translate("deactivate")} placement="top">
              <IconButton onClick={() => handleDeactivateEmailSubscription(subscription)}>
                <SmsNotificationError width={16} height={16} color={colors.gray600} />
              </IconButton>
            </Tooltip>
          );
        },
      });
    }

    return actions;
  };

  const rowActionsExtra: RowActionsExtra[] = handleRowActionsExtraByPermission();

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch"],
      right: ["handleDeactivateSubscription"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleDeactivateSubscription: isShowAction(currentUser, emailSubscriptionPermission.deactivate)
      ? returnButtonToDeactivateEmailSubscription()
      : undefined,
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  return (
    <div>
      {!isMoving(currentUser?.company) && (
        <SubHeader showCompanyVersion>
          <Breadcrumb items={getBreadCrumbItems()} />
        </SubHeader>
      )}

      <MainDiv>
        <ShipeeziNewGrid
          data={reportManagementList}
          loadingData={isFetching}
          columns={columns}
          totalItems={reportManagementTotal}
          emptyData={translate("noEmailSubscriptionsRegistered")}
          rowActionsExtra={rowActionsExtra}
          positionActionsColumn="right"
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          currentPage={page}
          onChangePageSize={setPageSize}
          onNextPage={setCurrentPage}
          onPreviousPage={setCurrentPage}
          onGoToPage={setCurrentPage}
          showFooterGrid
          showHeaderGrid
          showActionLabel
          handleSelect={(rowsArr: number[]) => handleSaveCronJobItems(rowsArr)}
          generalActions={generalActions}
        />
      </MainDiv>

      <ConfirmationModal
        open={openDeactivateModal}
        width="625px !important"
        closeModal={() => setOpenDeactivateModal(false)}
        type="error"
        title={translate("deactivateEmailScheduleForThisReport")}
        leftIconTitle={<AiOutlineWarning color="#F04438" size={20} />}
        customColorCancel={Color.TEXT_INFO}
        customColorSave={Color.ERROR}
        loading={isDeactivatingCronJob}
        textSubmit={translate("yesDeactivate")}
        textCancel={translate("cancelGoBack")}
        submit={() => selectedCronJob && deactivateSubscription(selectedCronJob)}
      >
        <ModalText>
          {translate("areYouSureYouWantToDeactivateThisReportEmailSubscription")}
        </ModalText>
        <ModalText>{translate("youWillNoLongerReceiveEmailsForTheSelectedReport")}</ModalText>
      </ConfirmationModal>

      <ManageEmailSubscriptionEditorDrawer
        ref={manageEmailSubscriptionEditorDrawerRef}
        isLoading={isUpdating}
        submitCronJob={(cronJob, changes) =>
          cronJob &&
          updateManagement({
            ...cronJob,
            changes,
          })
        }
      />

      <ManageEmailSubscriptionViewerDrawer ref={manageEmailSubscriptionViewerDrawerRef} />
    </div>
  );
};
